import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { addCardFact } from '../../api/cards';
import { cancelRun, getRun, listCardRuns, resolveFindings, startCardRuns } from '../../api/reviews';
import {
    additionHtml, editedFixHtml, editedParts, fixParts, htmlText, plainText, sentenceContext,
} from '../../services/findingText';
import { occurrenceBefore } from '../../services/editorMarks';
import ActiveFinding from './ActiveFinding';
import { AllHandledView, ErrorView, IdleView, OfferView, RunningView } from './CheckViews';
import {
    ActionBar, CategoryDone, CategoryTabs, CollapsedRow, ResolvedRow, ResultsHeader, TriageFooter,
} from './TriageParts';
import {
    categoryGroups,
    categoryInfo,
    checkFindings,
    checkPhase,
    checkProgress,
    failedRuns,
    firstOpenIn,
    hasApplicableFix,
    isOpen,
    latestCheck,
    markResolved,
    nextOpenAfter,
    replaceRuns,
    stepCategory,
    stepOpen,
} from './reviewRuns';
import useTriageKeys from './useTriageKeys';
import './ContentReviewPanel.css';

const POLL_INTERVAL = 2000;
const COPIED_MS = 1400;
const RUN_KINDS = { proofread: 'proofread', full: 'review' };

// The editor's review panel (CONTEXT.md "Check"): runs a proofread or a
// full review of the card, then triages the findings one at a time. The
// toolbar asks for a check through the ref (`check('proofread' | 'full')`)
// but never starts one: the panel shows the card's latest check of that
// mode, or offers to start it; checks start from the panel. The panel
// shows the active finding in the editor (onShowFinding), applies fixes
// (onApplyFix, onApplyFixes), reports the card's open findings
// (onOpenCount) and, once a card is done, offers the next card of a course
// check (nextCard, onNextCard).
function ContentReviewPanel(
    {
        cardId, token, bodyHtml, visible, progressHost, beforeRun,
        onStatusChange, onFactsChange, onApplyFix, onApplyFixes, onShowFinding,
        onOpenCount, nextCard, otherCards, onNextCard,
    },
    ref,
) {
    const [runs, setRuns] = useState(null); // null while loading
    // The card `runs` were loaded for: until the new card's runs arrive,
    // the panel is loading, never showing (or reporting) the last card's.
    const [runsCardId, setRunsCardId] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [now, setNow] = useState(Date.now());
    const [reviewStartedAt, setReviewStartedAt] = useState(null);
    // Triage: undefined picks the first open finding / its category.
    const [activeKey, setActiveKey] = useState(undefined);
    const [tab, setTab] = useState(undefined);
    const [fixes, setFixes] = useState({}); // key -> { html, tag: 'Edited' | 'Refined' }
    const [stale, setStale] = useState({}); // key -> true once Apply found the text changed
    const [editingKey, setEditingKey] = useState(null);
    const [editError, setEditError] = useState('');
    const [refineKey, setRefineKey] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [infoOpen, setInfoOpen] = useState(false);
    // A check picked in the toolbar that isn't the card's latest one,
    // waiting for Start.
    const [offeredMode, setOfferedMode] = useState(null);
    // Bumped to re-read the card's runs (after a course check started them).
    const [loadVersion, setLoadVersion] = useState(0);

    const resetTriage = useCallback(() => {
        setActiveKey(undefined);
        setTab(undefined);
        setFixes({});
        setStale({});
        setEditingKey(null);
        setEditError('');
        setRefineKey(null);
        setInfoOpen(false);
        setReviewStartedAt(null);
        setOfferedMode(null);
    }, []);

    const cardRuns = runsCardId === cardId ? runs : null;
    const check = useMemo(() => (cardRuns ? latestCheck(cardRuns) : null), [cardRuns]);
    const phase = cardRuns === null ? 'loading' : checkPhase(check);
    const findings = useMemo(() => checkFindings(check), [check]);
    const groups = categoryGroups(findings);
    const runningIds = check ? check.runs.filter(run => run.status === 'running').map(run => run.id).join(',') : '';
    const proofreadRun = check?.runs.find(run => run.kind === 'proofread');
    // A full review's review pass starts once its proofread run is over.
    const reviewStarted = check?.mode === 'full' && proofreadRun?.status !== 'running';

    // Load the card's runs whenever the card changes.
    useEffect(() => {
        setRuns(null);
        setErrorMsg('');
        resetTriage();
        if (!cardId || !token) {
            setRuns([]);
            setRunsCardId(cardId);
            return undefined;
        }
        let cancelled = false;
        listCardRuns(token, cardId)
            .then((list) => {
                if (cancelled) return;
                setRuns(list);
                setRunsCardId(cardId);
            })
            .catch(() => {
                if (cancelled) return;
                setRuns([]);
                setRunsCardId(cardId);
            });
        return () => { cancelled = true; };
    }, [cardId, token, resetTriage, loadVersion]);

    // Poll the running runs until each finishes; tick the elapsed clock.
    useEffect(() => {
        if (!runningIds) return undefined;
        const ids = runningIds.split(',').map(Number);
        const poll = setInterval(async () => {
            const updated = await Promise.all(ids.map(id => getRun(token, id).catch(() => null)));
            setRuns(prev => prev && replaceRuns(prev, updated.filter(Boolean)));
        }, POLL_INTERVAL);
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => { clearInterval(poll); clearInterval(tick); };
    }, [runningIds, token]);

    // A full review's stages switch from proofreading to reviewing when its
    // proofread run finishes.
    useEffect(() => {
        if (phase === 'running' && reviewStarted && reviewStartedAt === null) setReviewStartedAt(Date.now());
    }, [phase, reviewStarted, reviewStartedAt]);

    const mode = offeredMode ?? check?.mode ?? null;
    useEffect(() => {
        if (onStatusChange) onStatusChange({ mode, running: phase === 'running' });
    }, [mode, phase, onStatusChange]);

    // The card's open findings, for the course walk; unknown while loading
    // or while a new check runs.
    const openCount = phase === 'done' ? findings.filter(isOpen).length : 0;
    useEffect(() => {
        if (onOpenCount && cardId && (phase === 'done' || phase === 'idle' || phase === 'error')) {
            onOpenCount(cardId, openCount);
        }
    }, [cardId, phase, openCount, onOpenCount]);

    // --- Starting and stopping ---

    const start = useCallback(async (checkMode) => {
        if (!cardId) return;
        setErrorMsg('');
        // A check reads the saved body, so unsaved edits are saved first.
        if (beforeRun && !(await beforeRun())) {
            setErrorMsg('The card could not be saved, so the check would miss your latest edits. Save it, then try again.');
            return;
        }
        try {
            const started = await startCardRuns(token, cardId, RUN_KINDS[checkMode]);
            resetTriage();
            setNow(Date.now());
            setRuns(prev => [...started, ...(prev || [])]);
        } catch (err) {
            setErrorMsg(err.message || 'The check could not start.');
        }
    }, [cardId, token, beforeRun, resetTriage]);

    const requestCheck = useCallback((checkMode) => {
        setOfferedMode(check?.mode === checkMode ? null : checkMode);
    }, [check]);

    const reload = useCallback(() => setLoadVersion(v => v + 1), []);

    useImperativeHandle(ref, () => ({ check: requestCheck, reload }), [requestCheck, reload]);

    const cancel = () => {
        const ids = runningIds.split(',').map(Number);
        setRuns(prev => prev.map(run => (ids.includes(run.id) ? { ...run, status: 'cancelled' } : run)));
        ids.forEach(id => cancelRun(token, id).catch(() => {}));
    };

    // --- Finding text ---

    const fixHtmlOf = f => fixes[f.key]?.html ?? f.replacement_html ?? '';
    const anchorTextOf = f => plainText(f.anchor_html) || (f.claim_text || '').trim();
    const insertedText = (f, fixHtml) => (
        hasApplicableFix(f)
            ? fixParts(anchorTextOf(f), plainText(fixHtml), { addition: true })[0].text
            : (f.suggestion || '').trim()
    );
    const insertedTextOf = f => insertedText(f, fixHtmlOf(f));
    // The fix text as the check proposed it, before any edit or refine.
    const originalFixText = f => (
        f.category === 'suggestion' ? insertedText(f, f.replacement_html) : plainText(f.replacement_html)
    );

    const partsOf = (f) => {
        const addition = f.category === 'suggestion';
        if (addition) return [{ type: 'added', text: insertedTextOf(f) }];
        if (!hasApplicableFix(f)) {
            return f.suggestion ? fixParts(anchorTextOf(f), f.suggestion) : [{ type: 'same', text: anchorTextOf(f) }];
        }
        const fixText = plainText(fixHtmlOf(f));
        return fixes[f.key]?.tag === 'Edited' ? editedParts(anchorTextOf(f), fixText) : fixParts(anchorTextOf(f), fixText);
    };

    // The rest of the sentence around the active finding, read from the
    // editor's current text (or the checked text once the anchor is gone).
    const contextOf = (f) => {
        if (!f.anchor_html) return { pre: '', post: '' };
        const snapshot = check.runs.find(run => run.id === f.runId)?.content_snapshot;
        const context = sentenceContext(bodyHtml, f.anchor_html) || sentenceContext(snapshot, f.anchor_html)
            || { pre: '', post: '' };
        if (f.category !== 'suggestion') return context;
        // An addition goes after its anchor sentence, which reads as context.
        return { pre: `${context.pre}${anchorTextOf(f)} `, post: context.post };
    };

    const resolvedTextOf = (f) => {
        if (f.category === 'suggestion') return insertedTextOf(f);
        if (f.resolution === 'applied') return plainText(fixHtmlOf(f)) || f.suggestion || anchorTextOf(f);
        return anchorTextOf(f);
    };

    const copyTextOf = (f) => {
        if (f.category === 'suggestion') return insertedTextOf(f);
        return hasApplicableFix(f) ? plainText(fixHtmlOf(f)) : (f.suggestion || anchorTextOf(f));
    };

    // --- Triage state ---

    const active = activeKey === undefined
        ? findings.find(isOpen) || null
        : findings.find(f => f.key === activeKey && isOpen(f)) || null;
    const currentTab = groups.some(g => g.key === tab) ? tab : (active?.category ?? groups[0]?.key);
    const tabGroup = groups.find(g => g.key === currentTab);
    const canApplyFix = f => hasApplicableFix(f) && !stale[f.key];

    const select = (f) => {
        setActiveKey(f ? f.key : null);
        if (f) setTab(f.category);
        setEditingKey(null);
        setEditError('');
        setRefineKey(null);
    };

    const selectTab = (category) => {
        setTab(category);
        setActiveKey(firstOpenIn(findings, category)?.key ?? null);
        setEditingKey(null);
        setRefineKey(null);
    };

    const resolve = async (runId, findingIds, resolution) => {
        const before = runs.find(run => run.id === runId);
        setRuns(prev => markResolved(prev, runId, findingIds, resolution));
        try {
            const updated = await resolveFindings(token, runId, findingIds, resolution);
            setRuns(prev => replaceRuns(prev, [updated]));
        } catch {
            setRuns(prev => replaceRuns(prev, [before]));
        }
    };

    const resolveAndAdvance = (f, resolution) => {
        select(nextOpenAfter(findings, f.key, [f.key]));
        resolve(f.runId, [f.id], resolution);
    };

    const apply = async (f) => {
        const fixHtml = fixHtmlOf(f);
        if (canApplyFix(f) && !onApplyFix(f.anchor_html, fixHtml)) {
            setStale(prev => ({ ...prev, [f.key]: true }));
            return;
        }
        const appliedFix = canApplyFix(f);
        resolveAndAdvance(f, 'applied');
        // An applied factual/outdated fix carries its citation: save it to
        // the card's facts, recording the text actually inserted.
        if (appliedFix && f.source_url && (f.category === 'factual' || f.category === 'outdated')) {
            try {
                const facts = await addCardFact(token, cardId, {
                    text: plainText(fixHtml), source_url: f.source_url, source_title: '', from_review_id: f.runId,
                });
                if (onFactsChange) onFactsChange(facts);
            } catch {
                // The fix is applied either way; the fact can be saved by hand.
            }
        }
    };

    const applyAllTargets = findings.filter(f => f.category === 'proofread' && isOpen(f) && canApplyFix(f));

    const applyAll = () => {
        const appliedKeys = onApplyFixes(applyAllTargets.map(f => ({
            id: f.key, anchorHtml: f.anchor_html, replacementHtml: fixHtmlOf(f),
        })));
        const skipped = applyAllTargets.filter(f => !appliedKeys.includes(f.key));
        if (skipped.length) setStale(prev => ({ ...prev, ...Object.fromEntries(skipped.map(f => [f.key, true])) }));
        const applied = applyAllTargets.filter(f => appliedKeys.includes(f.key));
        if (!applied.length) return;
        select(findings.find(f => isOpen(f) && !appliedKeys.includes(f.key)) || null);
        resolve(applied[0].runId, applied.map(f => f.id), 'applied');
    };

    const startEdit = (f) => {
        setRefineKey(null);
        setEditError('');
        setEditingKey(f.key);
    };

    const finishEdit = (f, text) => {
        if (text.replace(/\s+/g, ' ').trim() === originalFixText(f)) {
            setFixes(({ [f.key]: _dropped, ...rest }) => rest);
            setEditingKey(null);
            return;
        }
        const html = f.category === 'suggestion'
            ? additionHtml(f.anchor_html, text)
            : editedFixHtml(fixHtmlOf(f), text);
        if (html === null) {
            setEditError('This edit cuts through formatting (bold, links). Make it in the editor instead.');
            return;
        }
        setFixes(prev => ({ ...prev, [f.key]: { html, tag: 'Edited' } }));
        setEditingKey(null);
        setEditError('');
    };

    const toggleRefine = (f) => {
        setEditingKey(null);
        setRefineKey(key => (key === f.key ? null : f.key));
    };

    const copy = (f) => {
        navigator.clipboard?.writeText(copyTextOf(f)).catch(() => {});
        setCopiedKey(f.key);
    };

    useEffect(() => {
        if (!copiedKey) return undefined;
        const timer = setTimeout(() => setCopiedKey(null), COPIED_MS);
        return () => clearTimeout(timer);
    }, [copiedKey]);

    // --- Keyboard triage ---

    // Triage happens on the results; an offer of another check hides them.
    const triaging = visible && !offeredMode && phase === 'done';

    useTriageKeys(triaging, (action) => {
        if (action === 'next' || action === 'prev') {
            const target = stepOpen(findings, active?.key, action === 'next' ? 1 : -1);
            if (target) select(target);
            return true;
        }
        if (action === 'nextTab' || action === 'prevTab') {
            const category = stepCategory(groups, currentTab, action === 'nextTab' ? 1 : -1);
            if (category) selectTab(category);
            return true;
        }
        if (!active) return false;
        if (action === 'apply') apply(active);
        else if (action === 'dismiss') resolveAndAdvance(active, 'declined');
        else if (action === 'edit' && hasApplicableFix(active)) startEdit(active);
        else if (action === 'refine' && hasApplicableFix(active)) toggleRefine(active);
        else if (action === 'copy') copy(active);
        else return false;
        return true;
    });

    // --- The active finding in the editor ---

    // What the editor marks while the panel is open: the active finding's
    // text, plus the sentence an addition would insert as ghost text.
    const showing = triaging && active ? active : null;
    const markText = showing ? anchorTextOf(showing) : '';
    const ghostText = showing?.category === 'suggestion' && hasApplicableFix(showing) ? insertedTextOf(showing) : '';
    const markState = useRef({});
    markState.current = { showing, markText, ghostText, bodyHtml };
    const markKey = showing ? `${showing.key}|${ghostText}` : '';

    useEffect(() => {
        if (!onShowFinding) return;
        const { showing: f, markText: text, ghostText: ghost, bodyHtml: html } = markState.current;
        if (!f) {
            onShowFinding(null);
            return;
        }
        const at = f.anchor_html && html ? html.indexOf(f.anchor_html) : -1;
        const occurrence = at > 0 ? occurrenceBefore(htmlText(html.slice(0, at)), text) : 0;
        onShowFinding({ text, occurrence, ghost: ghost || null });
    }, [markKey, onShowFinding]);

    // --- RENDER ---

    if (phase === 'loading') {
        return <div className="crp-panel"><div className="crp-center"><i className="pi pi-spin pi-spinner crp-run-spinner" /></div></div>;
    }

    const host = phase === 'running' && progressHost ? progressHost() : null;
    if (phase === 'running') {
        const startedAt = Math.min(...check.runs.map(run => (run.created_at || now / 1000) * 1000));
        const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
        const reviewSeconds = reviewStartedAt === null ? null : Math.max(0, Math.floor((now - reviewStartedAt) / 1000));
        const progress = checkProgress(check.mode, seconds, reviewSeconds);
        return (
            <div className="crp-panel">
                <RunningView mode={check.mode} stage={progress.stage} fraction={progress.fraction} seconds={seconds} onCancel={cancel} />
                {host && createPortal(
                    <div className="crp-editor-progress">
                        <div className="crp-editor-progress-fill" style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
                    </div>,
                    host,
                )}
            </div>
        );
    }

    if (offeredMode) {
        return (
            <div className="crp-panel">
                <OfferView
                    mode={offeredMode}
                    current={check?.mode}
                    errorMsg={errorMsg}
                    onStart={start}
                    onBack={() => setOfferedMode(null)}
                />
            </div>
        );
    }

    if (phase === 'idle') {
        return <div className="crp-panel"><IdleView errorMsg={errorMsg} /></div>;
    }

    if (phase === 'error') {
        return <div className="crp-panel"><ErrorView mode={check.mode} onRetry={() => start(check.mode)} /></div>;
    }

    const open = findings.filter(isOpen);
    if (open.length === 0) {
        const applied = findings.filter(f => f.resolution === 'applied').length;
        return (
            <div className="crp-panel">
                <AllHandledView
                    mode={check.mode}
                    applied={applied}
                    dismissed={findings.length - applied}
                    failedKind={failedRuns(check)[0]?.kind}
                    onRunFull={() => start('full')}
                    onRunAgain={() => start(check.mode)}
                    nextCard={nextCard}
                    otherCards={otherCards}
                    onNextCard={onNextCard}
                />
                {errorMsg && <p className="crp-error crp-error-bottom">{errorMsg}</p>}
            </div>
        );
    }

    const nextGroup = groups.find(g => g.key !== currentTab && g.open > 0);
    const applyLabel = !active ? 'Apply'
        : !canApplyFix(active) ? 'Mark fixed'
            : active.category === 'suggestion' ? 'Insert' : 'Apply';

    return (
        <div className="crp-panel">
            <ResultsHeader
                mode={check.mode}
                open={open.length}
                runs={check.runs}
                infoOpen={infoOpen}
                onToggleInfo={() => setInfoOpen(o => !o)}
                failed={failedRuns(check)}
                onRetry={() => start(check.mode)}
                onRunAgain={() => start(check.mode)}
            />
            {errorMsg && <p className="crp-error crp-error-head">{errorMsg}</p>}
            <CategoryTabs groups={groups} tab={currentTab} onSelect={selectTab} />

            <div className="crp-list">
                {(tabGroup?.findings || []).map((f) => {
                    if (!isOpen(f)) return <ResolvedRow key={f.key} finding={f} text={resolvedTextOf(f)} />;
                    if (f.key !== active?.key) {
                        return <CollapsedRow key={f.key} finding={f} parts={partsOf(f)} onSelect={select} />;
                    }
                    return (
                        <ActiveFinding
                            key={f.key}
                            finding={f}
                            color={categoryInfo(f.category)?.color}
                            parts={partsOf(f)}
                            context={contextOf(f)}
                            tag={fixes[f.key]?.tag}
                            canEdit={hasApplicableFix(f)}
                            canRefine={hasApplicableFix(f)}
                            copied={copiedKey === f.key}
                            currentFix={fixHtmlOf(f)}
                            editing={editingKey === f.key}
                            editText={f.category === 'suggestion' ? insertedTextOf(f) : plainText(fixHtmlOf(f))}
                            editError={editError}
                            onEdit={() => startEdit(f)}
                            onEditDone={text => finishEdit(f, text)}
                            onEditCancel={() => { setEditingKey(null); setEditError(''); }}
                            refineOpen={refineKey === f.key}
                            onRefine={() => toggleRefine(f)}
                            onRefined={(html) => {
                                setFixes(prev => ({ ...prev, [f.key]: { html, tag: 'Refined' } }));
                                setRefineKey(null);
                            }}
                            onCopy={() => copy(f)}
                            cardId={cardId}
                            token={token}
                            onFactsChange={onFactsChange}
                            onFactSaved={() => resolveAndAdvance(f, 'applied')}
                        />
                    );
                })}
                {tabGroup && tabGroup.open === 0 && (
                    <CategoryDone label={tabGroup.label} next={nextGroup} onNext={selectTab} />
                )}
            </div>

            <ActionBar
                disabled={!active}
                applyLabel={applyLabel}
                stale={!!(active && stale[active.key])}
                onDismiss={() => active && resolveAndAdvance(active, 'declined')}
                onApply={() => active && apply(active)}
            />
            <TriageFooter
                applyAllCount={currentTab === 'proofread' ? applyAllTargets.length : 0}
                onApplyAll={applyAll}
            />
        </div>
    );
}

export default forwardRef(ContentReviewPanel);
